import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import moment from 'moment';
import { ACTION_VIEW_SEARCH_TIMEOUT_MESSAGE } from '../actionView.adapters';
import { ActionViewApiService } from '../actionView.service';
import {
    ActionViewOverviewDataItem,
    ActionViewOverviewGroup,
    ActionViewOverviewResult,
    ActionViewOverviewTotals,
    ActionViewSearchRequest,
    TaskActionViewDateTimeOption,
    TaskActionViewOrganizeBy,
    TaskActionViewShowOption,
    TaskActionViewThatAreOption,
    TaskActionViewWhereOption,
} from '../actionView.models';

interface ActionViewOverviewRow extends ActionViewOverviewResult {
    totals: ActionViewOverviewTotals;
}

interface ActionViewOverviewSeries {
    key: string;
    name: string;
    taskSeriesTypeID: number;
    tables: ActionViewOverviewTable[];
}

interface ActionViewOverviewTable {
    key: string;
    name: string;
    rows: ActionViewOverviewRow[];
}

type Readiness = 'ready' | 'not ready' | null;

@Component({
    selector: 'action-view-overview',
    templateUrl: './actionViewOverview.component.html',
    styleUrls: ['./actionViewOverview.component.scss'],
    standalone: false,
})
export class ActionViewOverviewComponent implements OnChanges {
    constructor(private readonly actionViewService: ActionViewApiService) {}

    @Input({ required: true }) criteria: ActionViewSearchRequest;
    @Input() results: ActionViewOverviewResult[] = [];
    @Input() isTimedOut: boolean = false;
    @Output() drilldown = new EventEmitter<ActionViewSearchRequest>();

    timeoutMessage: string = ACTION_VIEW_SEARCH_TIMEOUT_MESSAGE;
    headers: string[] = [];
    series: ActionViewOverviewSeries[] = [];
    organizeTotals: ActionViewOverviewDataItem[] = [];
    grandTotals: ActionViewOverviewTotals = { value: 0, ready: 0, notReady: 0 };
    groupByCount: 0 | 1 | 2 = 0;

    ngOnChanges(): void {
        const rows = this.results.map((result) => ({
            ...result,
            totals: this.total(result.data),
        }));
        this.headers = [...(rows[0]?.data ?? [])]
            .sort((left, right) => left.sequence - right.sequence)
            .map((item) => item.label);
        this.groupByCount = Math.min(this.criteria.groupBy?.length ?? 0, 2) as 0 | 1 | 2;
        this.series = this.toSeries(rows);
        this.organizeTotals = this.calculateColumnTotals(rows);
        this.grandTotals = this.total(this.organizeTotals);
    }

    rowLabel(row: ActionViewOverviewRow): string {
        if (this.groupByCount === 0) {
            return row.taskSeriesTypeName;
        }
        return this.groupByCount === 2 ? (row.secondGroup?.value ?? '') : (row.firstGroup?.value ?? '');
    }

    columnClass(sequence: number): string {
        if (this.criteria.organizeBy === TaskActionViewOrganizeBy.Urgency) {
            return this.actionViewService.getUrgencyColumnStyleClass(sequence);
        }
        if (this.criteria.organizeBy === TaskActionViewOrganizeBy.DueDate && sequence === 0) {
            return 'urgency-overdue';
        }
        return 'misc-background';
    }

    isColumnSelectable(header: string): boolean {
        if (this.criteria.organizeBy === TaskActionViewOrganizeBy.Urgency) {
            const urgency = this.actionViewService.getUrgencyChoiceByHeaderName(header);
            return !this.criteria.urgencyChoice || urgency === this.criteria.urgencyChoice;
        }
        if (this.criteria.organizeBy === TaskActionViewOrganizeBy.DueDate) {
            const dates = this.dueDates(header);
            return dates.from <= dates.to;
        }
        const activity = this.lastActivity(header);
        return activity.from <= activity.to;
    }

    drilldownCell(row: ActionViewOverviewRow, item: ActionViewOverviewDataItem, readiness: Readiness): void {
        let request = this.forRow(row);
        request = this.withOrganizeFilter(request, item.label);
        request = this.withReadiness(request, readiness);
        this.emitDrilldown(request);
    }

    drilldownRow(row: ActionViewOverviewRow, readiness: Readiness): void {
        this.emitDrilldown(this.withReadiness(this.forRow(row), readiness));
    }

    drilldownColumn(header: string, rows?: ActionViewOverviewRow[], readiness: Readiness = null): void {
        let request = this.withOrganizeFilter(structuredClone(this.criteria), header);
        if (rows?.length && this.groupByCount > 0) {
            request = this.withTask(
                request,
                rows[0].taskSeriesTypeID,
                this.groupByCount === 2 ? this.groupTaskType(rows[0]) : 0,
            );
        }
        this.emitDrilldown(this.withReadiness(request, readiness));
    }

    drilldownSeries(series: ActionViewOverviewSeries): void {
        this.emitDrilldown(this.withTask(structuredClone(this.criteria), series.taskSeriesTypeID, 0));
    }

    drilldownFirstGroup(table: ActionViewOverviewTable): void {
        const row = table.rows[0];
        if (row) {
            this.emitDrilldown(
                this.withTask(structuredClone(this.criteria), row.taskSeriesTypeID, this.groupTaskType(row)),
            );
        }
    }

    drilldownGrandTotal(readiness: Readiness): void {
        this.emitDrilldown(this.withReadiness(structuredClone(this.criteria), readiness));
    }

    private forRow(row: ActionViewOverviewRow): ActionViewSearchRequest {
        let request = structuredClone(this.criteria);
        if (this.groupByCount === 0) {
            return this.withTask(request, row.taskSeriesTypeID, row.taskTypeID);
        }
        if (this.groupByCount === 2 || !this.criteria.showMostPressingTasks) {
            request = this.withTask(request, row.taskSeriesTypeID, this.groupTaskType(row));
            if (this.groupByCount === 2 && row.secondGroup) {
                request = this.withGroupFilter(request, row.secondGroup);
            }
            return request;
        }
        request = this.withTask(request, row.taskSeriesTypeID, row.taskTypeID);
        return row.firstGroup ? this.withGroupFilter(request, row.firstGroup) : request;
    }

    private withTask(
        request: ActionViewSearchRequest,
        taskSeriesTypeID: number,
        taskTypeID: number,
    ): ActionViewSearchRequest {
        return {
            ...request,
            taskSeriesTypeID,
            taskTypeID: taskTypeID || request.taskTypeID,
        };
    }

    private withGroupFilter(request: ActionViewSearchRequest, group: ActionViewOverviewGroup): ActionViewSearchRequest {
        const groupBy = request.groupBy.find((fieldId) => fieldId !== 10001);
        if (!groupBy) {
            return request;
        }
        switch (groupBy) {
            case 10002:
                return {
                    ...request,
                    showChoice: TaskActionViewShowOption.SpecificIndividualsTasks,
                    showUserTeams: request.showTeams.length
                        ? request.showTeams.map((team) => ({
                              userID: group.id,
                              name: group.value,
                              teamID: team.teamID,
                          }))
                        : [{ userID: group.id, name: group.value }],
                    whereChoice: TaskActionViewWhereOption.Assigned,
                    additionalWhereFilters:
                        request.whereChoice === TaskActionViewWhereOption.UltimatelyResponsible
                            ? [
                                  {
                                      whereChoice: TaskActionViewWhereOption.UltimatelyResponsible,
                                      userID: null,
                                      userTeams: request.showUserTeams
                                          .filter((item) => Boolean(item.userID))
                                          .map((item) => ({
                                              userID: String(item.userID),
                                              teamID: item.teamID ?? null,
                                              isDefault: item.isDefault ?? null,
                                          })),
                                      teams: [],
                                  },
                              ]
                            : request.additionalWhereFilters,
                };
            case 10003:
                return {
                    ...request,
                    showChoice: TaskActionViewShowOption.SpecificTeamsTasks,
                    showTeams: [{ teamID: Number(group.id), name: group.value }],
                    whereChoice: request.whereChoice || TaskActionViewWhereOption.Assigned,
                };
            case 10004:
                return {
                    ...request,
                    showChoice: TaskActionViewShowOption.SpecificIndividualsTasks,
                    showUserTeams: [{ userID: group.id, name: group.value }],
                    whereChoice: request.whereChoice || TaskActionViewWhereOption.Assigned,
                };
            case 10005:
                return {
                    ...request,
                    showChoice: TaskActionViewShowOption.SpecificTeamsTasks,
                    showTeams: [{ teamID: Number(group.id), name: group.value }],
                    whereChoice: request.whereChoice || TaskActionViewWhereOption.Assigned,
                };
            default:
                return {
                    ...request,
                    filters: [
                        ...request.filters.filter((filter) => filter.columnId !== groupBy),
                        {
                            columnId: groupBy,
                            or: [
                                {
                                    operator: group.value ? 'equal' : 'blank',
                                    operatorName: group.value ? 'equal' : 'blank',
                                    value: group.value,
                                },
                            ],
                        },
                    ],
                };
        }
    }

    private withOrganizeFilter(request: ActionViewSearchRequest, header: string): ActionViewSearchRequest {
        switch (request.organizeBy) {
            case TaskActionViewOrganizeBy.Urgency:
                return {
                    ...request,
                    useUrgencyFilter: true,
                    urgencyChoice: this.actionViewService.getUrgencyChoiceByHeaderName(header),
                };
            case TaskActionViewOrganizeBy.DueDate:
                if (header === 'Unscheduled') {
                    return { ...request, onlyUnscheduled: true };
                }
                const dueDates = this.dueDates(header);
                return {
                    ...request,
                    dateTimeChoice: TaskActionViewDateTimeOption.DueDateRange,
                    includeUnscheduled: false,
                    dueDateFrom: dueDates.from,
                    dueDateTo: dueDates.to,
                };
            case TaskActionViewOrganizeBy.LastActivityDate:
                const activity = this.lastActivity(header);
                return {
                    ...request,
                    useLastActivityFilter: true,
                    lastActivityFrom: activity.from,
                    lastActivityTo: activity.to,
                };
        }
    }

    private withReadiness(request: ActionViewSearchRequest, readiness: Readiness): ActionViewSearchRequest {
        if (!readiness) {
            return request;
        }
        return {
            ...request,
            thatAreChoice:
                readiness === 'ready' ? TaskActionViewThatAreOption.Ready : TaskActionViewThatAreOption.NotReady,
            mostPressingNotReadyOnly: readiness === 'not ready',
        };
    }

    private emitDrilldown(request: ActionViewSearchRequest): void {
        this.drilldown.emit({ ...request, detailedResults: true });
    }

    private dueDates(header: string): { from: Date; to: Date } {
        const now = new Date();
        let from: Date;
        let to: Date;
        if (header === 'Overdue') {
            from = moment('1970-01-01').toDate();
            to = this.utcDisplayDate(moment(now).subtract(1, 'days'));
        } else if (header === 'Due Today') {
            from = this.utcDisplayDate(moment(now).startOf('day'));
            to = from;
        } else if (header.includes('+')) {
            from = this.utcDisplayDate(
                moment(now)
                    .add(Number(header.split('+')[0]), 'days')
                    .startOf('day'),
            );
            to = this.utcDisplayDate(moment(now).add(30, 'years').startOf('day'));
        } else {
            const [fromDays, toDays] = header.split(' ')[0].split('-').map(Number);
            from = this.utcDisplayDate(moment(now).add(fromDays, 'days').startOf('day'));
            to = this.utcDisplayDate(moment(now).add(toDays, 'days').startOf('day'));
        }

        if (this.criteria.dateTimeChoice === TaskActionViewDateTimeOption.DueWithinDays) {
            const maximum = this.utcDisplayDate(
                moment(now)
                    .add(this.criteria.dueWithinDays ?? 0, 'days')
                    .startOf('day'),
            );
            to = new Date(Math.min(to.getTime(), maximum.getTime()));
        } else if (
            this.criteria.dateTimeChoice === TaskActionViewDateTimeOption.DueDateRange &&
            this.criteria.dueDateFrom &&
            this.criteria.dueDateTo
        ) {
            from = new Date(Math.max(from.getTime(), new Date(this.criteria.dueDateFrom).getTime()));
            to = new Date(Math.min(to.getTime(), new Date(this.criteria.dueDateTo).getTime()));
        }
        return { from, to };
    }

    private lastActivity(header: string): { from: number; to: number } {
        let from: number;
        let to: number;
        if (header === 'Today') {
            from = to = 0;
        } else if (header === '1 Day') {
            from = to = 1;
        } else if (header === '2 Days') {
            from = to = 2;
        } else if (header === '31+ Days') {
            from = 31;
            to = 9999;
        } else {
            [from, to] = header.split(' ')[0].split('-').map(Number);
        }
        if (this.criteria.useLastActivityFilter) {
            from = Math.max(from, this.criteria.lastActivityFrom ?? 0);
            to = Math.min(to, this.criteria.lastActivityTo ?? 9999);
        }
        return { from, to };
    }

    private utcDisplayDate(value: moment.Moment): Date {
        const local = value.toDate();
        return value.subtract(local.getTimezoneOffset(), 'minutes').toDate();
    }

    private groupTaskType(row: ActionViewOverviewRow): number {
        return Number(row.firstGroup?.id) || row.taskTypeID;
    }

    private total(
        items: Array<Pick<ActionViewOverviewDataItem, 'value' | 'ready' | 'notReady'>>,
    ): ActionViewOverviewTotals {
        return items.reduce(
            (sum, item) => ({
                value: sum.value + (item.value ?? 0),
                ready: sum.ready + (item.ready ?? 0),
                notReady: sum.notReady + (item.notReady ?? 0),
            }),
            { value: 0, ready: 0, notReady: 0 },
        );
    }

    private calculateColumnTotals(rows: ActionViewOverviewRow[]): ActionViewOverviewDataItem[] {
        const totals = new Map<string, ActionViewOverviewDataItem>();
        rows.forEach((row) =>
            row.data.forEach((item) => {
                const current = totals.get(item.label) ?? {
                    label: item.label,
                    value: 0,
                    ready: 0,
                    notReady: 0,
                    sequence: item.sequence,
                };
                current.value += item.value ?? 0;
                current.ready += item.ready ?? 0;
                current.notReady += item.notReady ?? 0;
                totals.set(item.label, current);
            }),
        );
        return [...totals.values()].sort((left, right) => left.sequence - right.sequence);
    }

    private toSeries(rows: ActionViewOverviewRow[]): ActionViewOverviewSeries[] {
        if (this.groupByCount === 0) {
            return [{ key: 'all', name: '', taskSeriesTypeID: 0, tables: [{ key: 'all', name: '', rows }] }];
        }
        const grouped = new Map<number, ActionViewOverviewRow[]>();
        rows.forEach((row) => {
            grouped.set(row.taskSeriesTypeID, [...(grouped.get(row.taskSeriesTypeID) ?? []), row]);
        });
        return [...grouped.entries()].map(([taskSeriesTypeID, seriesRows]) => ({
            key: String(taskSeriesTypeID),
            name: seriesRows[0]?.taskSeriesTypeName ?? '',
            taskSeriesTypeID,
            tables: this.toTables(seriesRows),
        }));
    }

    private toTables(rows: ActionViewOverviewRow[]): ActionViewOverviewTable[] {
        if (this.groupByCount !== 2) {
            return [{ key: 'all', name: '', rows }];
        }
        const grouped = new Map<string, ActionViewOverviewTable>();
        rows.forEach((row) => {
            const key = row.firstGroup?.id ?? String(row.taskTypeID);
            const table = grouped.get(key) ?? {
                key,
                name: row.firstGroup?.value ?? row.name,
                rows: [],
            };
            table.rows.push(row);
            grouped.set(key, table);
        });
        return [...grouped.values()];
    }
}
